import React, { useMemo, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { lightTheme } from '~/configs/leTheme';
import MUIProvider from '~/contexts/MUIProvider';
import NativeLensActions from '~/nativeLenses/NativeLensActions';
import { nativeLensId } from '~/nativeLenses/nativeLensId';
import { useNativeLensPrint } from '~/nativeLenses/useNativeLensPrint';

import { Alert, Box, Button, Container, CssBaseline, Grid, Stack, ThemeProvider, Typography } from '@mui/material';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import PageChatLayout from '~/pages/AskRichardUnified/page/PageChatLayout';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';
import type { LensPrintTarget } from '~/pages/LeLens/print/lensPrintTypes';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import type ApiError from '~/types/ApiError';

import FilterSelect from './FilterSelect';
import KpiTile from './KpiTile';
import PivotTable, { type PivotRow } from './PivotTable';
import { DATE_KEY, isProblem, num, readCard, text } from './cards';
import { type PageSection, dailySnapshotPageView, snapshotEditFromBridge } from './pageAdapter';
import {
  DAILY_SNAPSHOT_PAGE_ID,
  SNAPSHOT_FILTERS,
  type SnapshotFilters,
  parseSnapshotFilters,
  resetSnapshotFilters,
  snapshotKpis,
  snapshotRequest,
  writeSnapshotFilters,
} from './snapshotModel';

const LENS_ID = nativeLensId(DAILY_SNAPSHOT_PAGE_ID);
const SOURCE = 'lux-analytics-prod.analytics.sales_with_refunds';
const ADJUSTED_NOTE =
  'Adjusted sales and margin, which include the bundled-experience adjustment, so totals differ slightly from the brand tables.';

const sumRow = (label: string, rows: Array<PivotRow>, keys: Array<string>): PivotRow => ({
  label,
  values: Object.fromEntries(
    keys.map((key) => {
      const known = rows.map((row) => row.values[key]).filter((value): value is number => value !== null);
      return [key, known.length ? known.reduce((total, value) => total + value, 0) : null];
    }),
  ),
});

function DailySnapshotContent({ printTarget }: { printTarget?: LensPrintTarget; printFilters?: unknown }) {
  const root = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const filters = useMemo(() => parseSnapshotFilters(new URLSearchParams(location.search)), [location.search]);
  const update = (next: SnapshotFilters) =>
    navigate({
      pathname: location.pathname,
      search: writeSnapshotFilters(next, new URLSearchParams(location.search)).toString(),
    });
  const request = useMemo(() => snapshotRequest(filters), [filters]);
  const query = useGetReportingLeLensData(
    LENS_ID,
    request,
    true,
    undefined,
    undefined,
    undefined,
    undefined,
    printTarget ? 'json' : 'stream',
  );
  const cards = query.data?.cards ?? [];
  const lastOptions = useRef<Record<string, Array<string>>>({});
  if (query.data?.filterOptions && Object.keys(query.data.filterOptions).length) {
    lastOptions.current = query.data.filterOptions;
  }
  const options = lastOptions.current;
  const ready = query.isSuccess && !query.isFetching && !query.isShowingPreviousData;
  const loading = !ready;
  const failure = query.error as ApiError | null;
  const cardFailure = cards.find((card) => !['ready', 'empty'].includes(card.status));
  const { printing, downloadPdf } = useNativeLensPrint({
    root,
    ready,
    error: failure?.message ?? (ready && cardFailure ? (cardFailure.message ?? 'Report data could not load.') : null),
    target: printTarget,
    title: 'Daily Snapshot | Sales & Gross Margin',
    expectedCardCount: 16,
  });

  const kpiCard = cards.find((card) => card.cardId === 'kpis');
  const yesterday = kpiCard?.periods?.find((period) => period.id === 'yesterday')?.period.current.to ?? null;
  const kpis = yesterday ? snapshotKpis(kpiCard, yesterday) : null;
  const problemOf = (id: string) => {
    const result = readCard(cards, id);
    return isProblem(result) ? (query.isFetching ? 'Loading…' : result.problem) : null;
  };

  const brandTable = (id: string): Array<PivotRow> => {
    const result = readCard(cards, id);
    return isProblem(result)
      ? []
      : result.rows
          .map((row) => ({
            label: text(row.brand),
            values: { sales: num(row.gross_sales_br_aud), margin: num(row.gross_margin_ar_aud) },
          }))
          .sort((a, b) => (b.values.sales ?? 0) - (a.values.sales ?? 0));
  };
  const categoryTable = (id: string): Array<PivotRow> => {
    const result = readCard(cards, id);
    return isProblem(result)
      ? []
      : result.rows
          .map((row) => ({
            label: `${text(row.category)} · ${text(row.product)}`,
            values: { sales: num(row.gross_sales_adj_br_aud), margin: num(row.gross_margin_adj_ar_aud) },
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
  };
  const payments = (() => {
    const result = readCard(cards, 'mtd_payments');
    return isProblem(result)
      ? []
      : result.rows.map((row) => ({
          label: text(row[DATE_KEY]),
          values: {
            cashSales: num(row.cash_sales_aud),
            creditSales: num(row.credit_sales_aud),
            cashRefunds: num(row.cash_refunds_aud),
            creditRefunds: num(row.credit_refunds_aud),
          },
        }));
  })();
  const weekly = (() => {
    const result = readCard(cards, 'fytd_weekly');
    return isProblem(result)
      ? []
      : result.rows.map((row) => ({
          label: text(row[DATE_KEY]),
          values: { sales: num(row.gross_sales_br_aud), margin: num(row.gross_margin_ar_aud) },
        }));
  })();

  const salesMargin = [
    { key: 'sales', label: 'Gross Sales (BR)', format: 'currency' as const },
    { key: 'margin', label: 'Gross Margin (AR)', format: 'currency' as const },
  ];
  const adjusted = [
    { key: 'sales', label: 'Gross Sales Adj. (BR)', format: 'currency' as const },
    { key: 'margin', label: 'Gross Margin Adj. (AR)', format: 'currency' as const },
  ];
  const paymentColumns = [
    { key: 'cashSales', label: 'Cash Sales', format: 'currency' as const },
    { key: 'creditSales', label: 'Credit Sales', format: 'currency' as const },
    { key: 'cashRefunds', label: 'Cash Refunds', format: 'currency' as const },
    { key: 'creditRefunds', label: 'Credit Refunds', format: 'currency' as const },
  ];
  const tables = {
    mtdBrand: brandTable('mtd_brand'),
    fytdBrand: brandTable('fytd_brand'),
    mtdCategory: categoryTable('mtd_category'),
    fytdCategory: categoryTable('fytd_category'),
  };

  const sections: Array<PageSection> = [
    { title: 'MTD by Brand', rows: tables.mtdBrand, truncated: false },
    { title: 'FYTD by Brand', rows: tables.fytdBrand, truncated: false },
    { title: 'MTD by Category', rows: tables.mtdCategory, truncated: false },
    { title: 'FYTD by Category', rows: tables.fytdCategory, truncated: false },
    { title: 'MTD All Transactions', rows: payments, truncated: false },
    { title: 'FYTD Weekly Trend', rows: weekly, truncated: false },
  ];
  const pageView = dailySnapshotPageView({
    ready,
    filters,
    options,
    summary: kpis ? { ...kpis } : null,
    sections,
  });
  usePageBridge({
    pageId: DAILY_SNAPSHOT_PAGE_ID,
    view: pageView,
    edit: (changes) => {
      if (!ready) {
        throw new Error('Page data is still loading.');
      }
      update(snapshotEditFromBridge(changes, pageView, filters));
    },
  });

  return (
    <Container ref={root} maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography data-lens-pdf-ignore={printing ? true : undefined} variant="h5">
              Daily Snapshot | Sales &amp; Gross Margin
            </Typography>
            <Typography variant="body2" color="text.secondary">
              BR = before refunds, AR = after refunds. {yesterday ? `Data to ${yesterday} (yesterday, Sydney).` : ''}
            </Typography>
          </Box>
          {printing ? (
            <Typography variant="body2">
              {SNAPSHOT_FILTERS.filter(({ key }) => filters[key].length)
                .map(({ key, label }) => `${label}: ${filters[key].join(', ')}`)
                .join(' · ') || 'All offer destinations'}
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {SNAPSHOT_FILTERS.map(({ key, label }) => (
                <FilterSelect
                  key={key}
                  label={label}
                  options={options[key] ?? []}
                  value={filters[key]}
                  onChange={(value) => update({ ...filters, [key]: value })}
                />
              ))}
              {!printTarget && (
                <NativeLensActions
                  onDownloadPdf={downloadPdf}
                  pageId={DAILY_SNAPSHOT_PAGE_ID}
                  filters={request}
                  filterOptions={options}
                  ready={ready && !cardFailure}
                />
              )}
              {!printTarget && <PageChatButton />}
              {!printTarget && <Button onClick={() => update(resetSnapshotFilters())}>Reset filters</Button>}
            </Stack>
          )}
        </Stack>
        {failure && <Alert severity="error">{failure.message ?? 'The snapshot could not load.'}</Alert>}

        <Grid container spacing={1.5}>
          {[
            { label: 'Gross Sales (BR) yesterday', value: kpis?.grossSales, format: 'currency' as const },
            { label: 'Days until end of month', value: kpis?.daysUntilEndOfMonth, format: 'count' as const },
            { label: 'MTD Gross Sales (BR)', value: kpis?.mtdGrossSales, format: 'currency' as const },
            { label: 'Gross Sales (BR) ROM forecast', value: kpis?.romGrossSales, format: 'currency' as const },
            { label: 'Gross Sales (BR) EOM forecast', value: kpis?.eomGrossSales, format: 'currency' as const },
            { label: 'Gross Margin (AR) yesterday', value: kpis?.grossMargin, format: 'currency' as const },
            { label: 'Gross Margin (AR) % yesterday', value: kpis?.grossMarginRate, format: 'percent' as const },
            { label: 'MTD Gross Margin (AR)', value: kpis?.mtdGrossMargin, format: 'currency' as const },
            { label: 'Gross Margin (AR) ROM forecast', value: kpis?.romGrossMargin, format: 'currency' as const },
            { label: 'Gross Margin (AR) EOM forecast', value: kpis?.eomGrossMargin, format: 'currency' as const },
          ].map((tile) => (
            <Grid key={tile.label} size={{ xs: 6, md: 12 / 5 }}>
              <KpiTile label={tile.label} value={tile.value ?? null} format={tile.format} loading={loading && !kpis} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <PivotTable
              title="MTD by Brand"
              rowLabel="Brand"
              columns={salesMargin}
              rows={tables.mtdBrand}
              total={sumRow('Total', tables.mtdBrand, ['sales', 'margin'])}
              problem={problemOf('mtd_brand')}
              csvName="daily-snapshot-mtd-brand.csv"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <PivotTable
              title="FYTD by Brand"
              rowLabel="Brand"
              columns={salesMargin}
              rows={tables.fytdBrand}
              total={sumRow('Total', tables.fytdBrand, ['sales', 'margin'])}
              problem={problemOf('fytd_brand')}
              csvName="daily-snapshot-fytd-brand.csv"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <PivotTable
              title="MTD by Category"
              subtitle={ADJUSTED_NOTE}
              rowLabel="Category · Product"
              columns={adjusted}
              rows={tables.mtdCategory}
              total={sumRow('Total', tables.mtdCategory, ['sales', 'margin'])}
              problem={problemOf('mtd_category')}
              csvName="daily-snapshot-mtd-category.csv"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <PivotTable
              title="FYTD by Category"
              subtitle={ADJUSTED_NOTE}
              rowLabel="Category · Product"
              columns={adjusted}
              rows={tables.fytdCategory}
              total={sumRow('Total', tables.fytdCategory, ['sales', 'margin'])}
              problem={problemOf('fytd_category')}
              csvName="daily-snapshot-fytd-category.csv"
            />
          </Grid>
          <Grid size={12}>
            <PivotTable
              title="MTD All Transactions"
              subtitle="Cash and credit sales and refunds by day, month to date."
              rowLabel="Day"
              columns={paymentColumns}
              rows={payments}
              total={sumRow(
                'Total',
                payments,
                paymentColumns.map((column) => column.key),
              )}
              problem={problemOf('mtd_payments')}
              csvName="daily-snapshot-mtd-transactions.csv"
              maxHeight={360}
            />
          </Grid>
          <Grid size={12}>
            <PivotTable
              title="FYTD Weekly Trend - All Transactions"
              subtitle="Gross Sales (BR) and Gross Margin (AR) by Monday week, financial year to date."
              rowLabel="Week starting"
              columns={salesMargin}
              rows={weekly}
              problem={problemOf('fytd_weekly')}
              csvName="daily-snapshot-fytd-weekly.csv"
              maxHeight={360}
            />
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere', pb: 2 }}>
          Source: {SOURCE}. Rest of month forecast = average daily value over the 21 days to yesterday × days until end
          of month (0 on the last day of the month). End of month forecast = MTD + rest of month (rest of month alone on
          the 1st). Gross Margin (AR) % = Gross Margin (AR) ÷ (gross sales − accounting refunds).
        </Typography>
      </Stack>
    </Container>
  );
}

export default function DailySnapshotPage({ printTarget }: { printTarget?: LensPrintTarget; printFilters?: unknown }) {
  if (printTarget)
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <DailySnapshotContent printTarget={printTarget} />
      </ThemeProvider>
    );
  return (
    <MUIProvider>
      <PageChatLayout>
        <DailySnapshotContent />
      </PageChatLayout>
    </MUIProvider>
  );
}
